<template>
    <div class="dialog-card">
        <!-- Header with gradient using Header Color -->
        <div class="dialog-header" :style="dialogHeaderStyle">
            <div class="d-flex align-center">
                <img :src="SnippedLogo || logoRemovebg" alt="logo" class="dialog-logo">
            </div>
            <div class="d-flex align-center">
                <v-btn icon size="small" variant="text" :style="{ color: '#FFFFFF', height: '30px', width: '30px' }">
                    <v-icon>mdi-magnify-plus-outline</v-icon>
                </v-btn>
                <v-btn icon size="small" variant="text" :style="{ color: '#FFFFFF', height: '30px', width: '30px' }">
                    <v-icon>mdi-window-minimize</v-icon>
                </v-btn>
            </div>
        </div>

        <!-- Sub-header with white background -->
        <div class="dialog-subheader">
            <span :style="{ fontSize: '10px', fontWeight: 500, color: '#000000' }">Model 1 (Azure GPT 4o)</span>
            <v-btn variant="outlined" size="small" :style="loginBtnStyle">
                Login
            </v-btn>
        </div>

        <!-- Chat conversation area -->
        <div class="dialog-chat-area">
            <!-- User message -->
            <div class="d-flex justify-end align-center mb-2">
                <span class="mr-2" style="font-size: 8px; color: #71717A;">22 Oct, 10:32 AM</span>
                <div class="user-message" :style="userMessageStyle">Hello</div>
            </div>

            <!-- GPT response -->
            <div class="d-flex justify-start align-start mb-2 bot-preview-row">
                <div class="bot-preview-avatar-shell" :class="{ 'bot-preview-avatar-shell--hidden': !hasSnippedAvatar }">
                    <img v-if="shouldUseSnippedAvatarImage" :src="resolvedSnippedIconImage" alt="Bot" class="bot-preview-avatar">
                    <v-icon v-else-if="hasSnippedAvatar" size="18" :style="{ color: resolvedSnippedIconColor }">{{ resolvedSnippedIconName }}</v-icon>
                </div>
                <div class="bot-preview-content">
                    <div class="gpt-message">Hi! How can I help you today?</div>
                    <span class="bot-preview-time">22 Oct, 10:32 AM</span>
                </div>
            </div>

            <!-- Another user message -->
            <div class="d-flex justify-end align-center mb-2">
                <span class="mr-2" style="font-size: 8px; color: #71717A;">22 Oct, 10:33 AM</span>
                <div class="user-message" :style="userMessageStyle">I need help with my account settings</div>
            </div>
        </div>

        <!-- Message input area -->
        <div class="dialog-footer">
            <v-textarea
                placeholder="Type your message here..."
                variant="outlined"
                rows="1"
                hide-details
                class="dialog_text_area"
                style="font-size: 8px;"
            ></v-textarea>
            <div class="d-flex justify-end mt-1">
                <span style="font-size: 8px; color: #71717A;">0/200000</span>
            </div>
        </div>
    </div>
</template>

<script>
import logoRemovebg from '@/assets/images/logo-removebg-preview@3x.png';

/*
 * SnippedPreview.vue — live preview (client-side only, no API call).
 *
 * หมายเหตุ (25 ส.ค. 2026): หน้าตาของ preview ถูก hard revert กลับ baseline 7d3b0c4fc
 * เพราะลูกค้าขอถอดฟีเจอร์ snippet ชุดที่พอร์ตจาก supalai (3b0edc8b1..ad4a3accf) ออกก่อน
 * props ยังประกาศครบทุกตัวโดยตั้งใจ เพื่อให้ ConfigTheme.vue (snippedPreviewProps) v-bind ได้เหมือนเดิม
 * ตัวที่เป็นของ supalai แค่ "ไม่ถูกนำมาวาด" ห้ามลบทิ้ง — วิธีเอากลับดู docs/snippet-supalai-ui-hide-plan.md ข้อ 8
 * ที่ preview วาดจริงตอนนี้เหลือ 6 ตัว: SnippedLogo, SnippedHeaderColorTheme, SnippedFontColorTheme,
 * SnippedColorThemeChat, SnippedIcon, SnippedIconColor — กลุ่ม Launcher / Teaser / Panel /
 * display settings และ botName ประกาศไว้เฉย ๆ ไม่ถูกนำมาวาด
 *
 * Prop contract for the parent (ConfigTheme.vue / Task T5) — bind case-exact PascalCase
 * (e.g. :SnippedLauncherShape="ConfigData.SnippedLauncherShape"). These mirror the API
 * `Snipped*` field names 1:1.
 *
 *   Base / existing:
 *     SnippedLogo, SnippedColorThemeChat, SnippedIcon (mdi name OR blob/url — img derived
 *     via URL-like check), SnippedIconColor, SnippedHeaderColorTheme, SnippedFontColorTheme
 *   Launcher (9 + position/offset):
 *     SnippedLauncherShape, SnippedLauncherVariant, SnippedLauncherText, SnippedLauncherTextColor,
 *     SnippedLauncherBgColor, SnippedLauncherIcon (mdi name OR blob url), IsImgSnippedLauncherIcon,
 *     SnippedLauncherPosition, SnippedLauncherOffsetX, SnippedLauncherOffsetY
 *   Teaser (3):
 *     SnippedTeaserEnabled, SnippedTeaserBgColor, SnippedTeaserTextColor
 *   Panel (7):
 *     SnippedHeaderSubtitle, SnippedDisclaimerText, SnippedPanelBorderColor, SnippedInputPlaceholder,
 *     SnippedTypingText, SnippedShowBotName, SnippedShowModelTopicBar (default-on: show unless === false)
 *   Display settings (9):
 *     SnippedBotBubbleColor, SnippedBotFontColor, SnippedSendButtonColor, SnippedSendButtonVariant,
 *     SnippedShowHeaderSubtitle, SnippedShowVersion, SnippedShowCharCounter, SnippedShowSnippedIcon,
 *     SnippedShowLauncherIcon
 *   Auxiliary (NOT a theme field): botName (actual bot display name, optional)
 *
 * Nullable bool props (SnippedTeaserEnabled / SnippedShowBotName / SnippedShowModelTopicBar /
 * IsImgSnippedLauncherIcon) come from the API as bool? — pass real booleans (or leave undefined);
 * the component treats null/undefined as the default (off, except ShowModelTopicBar = on).
 * All colors run through normalizeHexColor(#RRGGBB) with a fallback, so partial/invalid hex
 * never breaks the preview. Launcher/bot images use blob/url strings (no base64).
 */
export default {
    props: {
        // ── Base / existing ──
        SnippedLogo: {
            type: String,
            required: false,
            default: ''
        },
        SnippedColorThemeChat: {
            type: String,
            required: false,
            default: '#C32E0E'
        },
        SnippedHeaderColorTheme: {
            type: String,
            required: false,
            default: '#C32E0E'
        },
        SnippedFontColorTheme: {
            type: String,
            required: false,
            default: '#FFFFFF'
        },
        // Bot avatar icon — single field holding mdi name OR blob/url (img derived via URL-like check)
        SnippedIcon: {
            type: String,
            required: false,
            default: 'mdi-robot-outline'
        },
        SnippedIconColor: {
            type: String,
            required: false,
            default: '#000000'
        },
        // ── Launcher ──
        SnippedLauncherShape: {
            type: String,
            required: false,
            default: 'circle'
        },
        // [D17] 'default' | 'secondary' | 'outline' — determines surface/border/foreground
        SnippedLauncherVariant: {
            type: String,
            required: false,
            default: 'default'
        },
        SnippedLauncherText: {
            type: String,
            required: false,
            default: ''
        },
        SnippedLauncherTextColor: {
            type: String,
            required: false,
            default: '#FFFFFF'
        },
        SnippedLauncherBgColor: {
            type: String,
            required: false,
            default: ''
        },
        // Launcher icon — single field holding mdi name OR blob url; img mode driven by IsImgSnippedLauncherIcon
        SnippedLauncherIcon: {
            type: String,
            required: false,
            default: ''
        },
        IsImgSnippedLauncherIcon: {
            type: Boolean,
            required: false,
            default: false
        },
        // Hint-only (real placement happens on the live widget)
        SnippedLauncherPosition: {
            type: String,
            required: false,
            default: 'bottom-right'
        },
        SnippedLauncherOffsetX: {
            type: [Number, String],
            required: false,
            default: null
        },
        SnippedLauncherOffsetY: {
            type: [Number, String],
            required: false,
            default: null
        },
        // ── Teaser ──
        SnippedTeaserEnabled: {
            type: Boolean,
            required: false,
            default: false
        },
        SnippedTeaserBgColor: {
            type: String,
            required: false,
            default: ''
        },
        SnippedTeaserTextColor: {
            type: String,
            required: false,
            default: ''
        },
        // ── Panel ──
        SnippedHeaderSubtitle: {
            type: String,
            required: false,
            default: ''
        },
        SnippedDisclaimerText: {
            type: String,
            required: false,
            default: ''
        },
        // [D20] panel border · ว่าง = ไม่มีขอบ
        SnippedPanelBorderColor: {
            type: String,
            required: false,
            default: ''
        },
        SnippedInputPlaceholder: {
            type: String,
            required: false,
            default: ''
        },
        // ข้อความตอนบอทกำลังคิด · ว่าง = fallback 'Thinking of a response' (ตรงกับ Snippet.vue)
        SnippedTypingText: {
            type: String,
            required: false,
            default: ''
        },
        SnippedShowBotName: {
            type: Boolean,
            required: false,
            default: false
        },
        // [D3] default true — ตรงกับ Snippet.vue ที่เช็ค `!== false` (null/undefined = แสดงแถบ)
        SnippedShowModelTopicBar: {
            type: Boolean,
            required: false,
            default: true
        },
        // ── R2: display settings ──
        // Bot bubble uses the same 10% background / 20% border alpha treatment as the user bubble.
        SnippedBotBubbleColor: {
            type: String,
            required: false,
            default: '#71717A'
        },
        SnippedBotFontColor: {
            type: String,
            required: false,
            default: '#71717A'
        },
        // ว่าง = ใช้สีจาก SnippedColorThemeChat
        SnippedSendButtonColor: {
            type: String,
            required: false,
            default: ''
        },
        // 'default' | 'secondary' | 'outline' — same treatment as the launcher variant
        SnippedSendButtonVariant: {
            type: String,
            required: false,
            default: 'default'
        },
        // ทุกตัวด้านล่าง default true — ตรงกับ Snippet.vue ที่เช็ค `!== false` (null/undefined = แสดง)
        SnippedShowHeaderSubtitle: {
            type: Boolean,
            required: false,
            default: true
        },
        SnippedShowVersion: {
            type: Boolean,
            required: false,
            default: true
        },
        SnippedShowCharCounter: {
            type: Boolean,
            required: false,
            default: true
        },
        SnippedShowSnippedIcon: {
            type: Boolean,
            required: false,
            default: true
        },
        SnippedShowLauncherIcon: {
            type: Boolean,
            required: false,
            default: true
        },
        // ── Auxiliary (not a theme field) ──
        botName: {
            type: String,
            required: false,
            default: 'Chatbot'
        }
    },
    data() {
        return {
            logoRemovebg: logoRemovebg
        };
    },
    computed: {
        dialogHeaderStyle() {
            const baseColor = this.normalizeHexColor(this.SnippedHeaderColorTheme, '#C32E0E');
            const endColor = this.lightenColor(baseColor);

            return {
                background: `linear-gradient(90deg, ${baseColor} 0%, ${endColor} 100%)`,
                backgroundPosition: 'center',
                backgroundSize: 'cover',
            };
        },
        loginBtnStyle() {
            const baseColor = this.normalizeHexColor(this.SnippedHeaderColorTheme, '#C32E0E');
            const lightColor = this.lightenColor(baseColor);

            return {
                background: '#FFFFFF',
                borderColor: lightColor,
                color: baseColor,
                textTransform: 'none',
                borderRadius: '8px',
            };
        },
        userMessageStyle() {
            const raw = typeof this.SnippedColorThemeChat === 'string' ? this.SnippedColorThemeChat.trim() : '';
            const background = raw !== ''
                ? `${this.normalizeHexColor(raw, '#C32E0E')}1A`
                : 'rgba(195, 46, 14, 0.10)';

            return {
                background: background,
                color: this.normalizeHexColor(this.SnippedFontColorTheme, '#FFFFFF'),
            };
        },
        // ── Bot avatar (single SnippedIcon field; img vs mdi derived from URL-like value) ──
        resolvedSnippedIcon() {
            return typeof this.SnippedIcon === 'string' ? this.SnippedIcon.trim() : '';
        },
        hasSnippedAvatar() {
            return this.resolvedSnippedIcon !== '';
        },
        shouldUseSnippedAvatarImage() {
            return this.isUrlLikeIcon(this.resolvedSnippedIcon);
        },
        resolvedSnippedIconImage() {
            return this.shouldUseSnippedAvatarImage ? this.resolvedSnippedIcon : '';
        },
        resolvedSnippedIconName() {
            return this.hasSnippedAvatar && !this.shouldUseSnippedAvatarImage ? this.resolvedSnippedIcon : 'mdi-robot-outline';
        },
        resolvedSnippedIconColor() {
            return this.normalizeHexColor(this.SnippedIconColor, '#000000');
        },
    },
    methods: {
        isUrlLikeIcon(value) {
            if (typeof value !== 'string') {
                return false;
            }

            const normalizedValue = value.trim();
            return normalizedValue.startsWith('http://')
                || normalizedValue.startsWith('https://')
                || normalizedValue.startsWith('data:')
                || normalizedValue.startsWith('blob:');
        },
        normalizeHexColor(color, fallback) {
            const normalizedColor = typeof color === 'string' ? color.trim() : '';
            const colorWithHash = normalizedColor.startsWith('#') ? normalizedColor : `#${normalizedColor}`;

            return /^#[0-9A-Fa-f]{6}$/.test(colorWithHash) ? colorWithHash : fallback;
        },
        lightenColor(color) {
            const normalizedColor = this.normalizeHexColor(color, '#C32E0E');

            let hex = normalizedColor.replace('#', '');
            let r = Number.parseInt(hex.substring(0, 2), 16);
            let g = Number.parseInt(hex.substring(2, 4), 16);
            let b = Number.parseInt(hex.substring(4, 6), 16);

            r = Math.min(255, r + 50);
            g = Math.min(255, g + 50);
            b = Math.min(255, b + 50);

            return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
        }
    }
};
</script>
<style scoped>
    .dialog-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 350px;
  width: 80%;
  background: white;
  box-shadow: none;
  border-radius: 12px;
  border: 1px solid #E4E4E7;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-radius: 12px 12px 0 0;
}

.dialog-logo {
    height: 30px;
    width: auto;
    max-width: 60px;
    object-fit: contain;
}

.dialog-subheader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: white;
}

.dialog-chat-area {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  background: white;
}

.dialog-footer {
  padding: 8px;
  background: white;
}

.user-message {
  background: #f9eae7;
  color: #18181B;
  padding: 6px 10px;
  border-radius: 18px 2px 18px 18px;
  font-size: 10px;
  max-width: 80%;
  word-wrap: break-word;
}

.gpt-message {
  background: white;
  color: #18181B;
  padding: 6px 0px;
  border-radius: 18px 18px 18px 2px;
  font-size: 10px;
    max-width: 100%;
  word-wrap: break-word;
}

.bot-preview-row {
    gap: 6px;
}

.bot-preview-avatar-shell {
    width: 24px;
    height: 24px;
    min-width: 24px;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.bot-preview-avatar-shell--hidden {
    visibility: hidden;
}

.bot-preview-avatar {
    width: 100%;
    height: 100%;
    display: block;
    border-radius: inherit;
    object-fit: cover;
}

.bot-preview-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    max-width: calc(100% - 27px);
}

.bot-preview-time {
    margin-top: 2px;
    font-size: 8px;
    color: #71717A;
}

.dialog_text_area :deep(.v-field__input) {
  padding: 12px 4px;
  font-size: 10px;
}
.dialog_text_area :deep(.v-field) {
    border-radius: 14px;
    padding: 8px;
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
}
</style>
